import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Component, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { timeout } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { GlobalService } from '../../service/global.service';
import { AnalyticsService } from '../../service/analytics.service';
import { MetaPixelService } from '../../service/meta-pixel.service';
import { RealEmailValidatorDirective } from '../../directives/real-email-validator.directive';
import { TurnstileComponent } from '../../shared/turnstile/turnstile.component';

type PreventivoLead = {
  sede: 'pescara' | 'teramo';
  nome: string;
  cognome: string;
  indirizzo: string;
  email: string;
  telefono: string;
  descrizione: string;
  servizio: string;
};

@Component({
  selector: 'app-preventivo',
  standalone: true,
  imports: [CommonModule, FormsModule, RealEmailValidatorDirective, TurnstileComponent],
  templateUrl: './preventivo-module.html',
  styleUrls: ['./preventivo-module.scss']
})
export class PreventivoComponent {
  @ViewChild(TurnstileComponent) turnstile?: TurnstileComponent;
  turnstileToken = '';

  formData = {
    sede: '' as '' | 'pescara' | 'teramo',
    nome: '',
    cognome: '',
    indirizzo: '',
    email: '',
    telefono: '',
    servizio: '',
    descrizione: ''
  };

  privacyAccepted = false;
  showPrivacyModal = false;

  loading = false;
  successMsg = '';
  errorMsg = '';

  constructor(
    private http: HttpClient,
    private global: GlobalService,
    private metaPixel: MetaPixelService,
    private analytics: AnalyticsService
  ) {}

  openPrivacy(event: Event): void {
    // link nel form: apriamo il modal e non navighiamo via
    event.preventDefault();
    this.showPrivacyModal = true;
  }

  closePrivacy(): void {
    this.showPrivacyModal = false;
  }

  acceptPrivacy(): void {
    this.privacyAccepted = true;
    this.showPrivacyModal = false;
  }

  resetForm(form: NgForm): void {
    form.resetForm();
    this.privacyAccepted = false;
    this.formData = { sede: '', nome: '', cognome: '', indirizzo: '', email: '', telefono: '', servizio: '', descrizione: '' };
    this.successMsg = '';
    this.errorMsg = '';

  }

  onSubmit(form: NgForm): void {
    if (this.loading) return;
    this.successMsg = '';
    this.errorMsg = '';

    if (!this.privacyAccepted) {
      this.errorMsg = 'Devi accettare la Privacy Policy.';
      return;
    }

    if (form.pending) {
      this.errorMsg = 'Attendi un momento: stiamo verificando il dominio dell’email.';
      return;
    }

    if (form.invalid) {
      this.errorMsg = 'Controlla i campi evidenziati.';
      return;
    }
    if (!this.turnstileToken) { this.errorMsg = 'Completa la verifica di sicurezza.'; return; }

    const url = `${this.global.url}email/send`;
    const leadData = { ...this.formData } as PreventivoLead;

    this.loading = true;

    this.http.post<{ confirmationSent?: boolean }>(url, { ...leadData, turnstile_token: this.turnstileToken })
      .pipe(
        timeout(30000),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.trackLeadConversion(leadData);
          this.resetForm(form);
          this.successMsg = `Richiesta inviata alla sede di ${leadData.sede === 'pescara' ? 'Pescara' : 'Giulianova'}! Ti ricontatteremo per conoscere le tue esigenze e concordare il sopralluogo.`;
          if (response.confirmationSent === false) {
            this.successMsg += ' La conferma email non è stata inviata, ma la sede ha ricevuto la richiesta: non occorre inviarla di nuovo.';
          }
          this.turnstileToken = '';
          this.turnstile?.reset();
        },
        error: (error: HttpErrorResponse | Error) => {
          const fields = error instanceof HttpErrorResponse ? error.error?.fields : undefined;
          this.errorMsg = fields ? String(Object.values(fields)[0]) : error.name === 'TimeoutError'
            ? 'Il server non ha risposto entro 30 secondi. Riprova oppure chiama la sede di riferimento.'
            : 'Invio non riuscito. Riprova oppure contatta direttamente la sede selezionata.';
          this.turnstile?.reset();
        }
      });
  }

  private trackLeadConversion(leadData: PreventivoLead): void {
    this.analytics.trackLead(leadData.sede);
    if (!this.metaPixel.isAccepted) return;

    const eventId = this.createEventId();
    this.metaPixel.trackLead(eventId);

    this.http.post(`${this.global.url}meta/lead`, {
      ...leadData,
      eventId,
      eventSourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    }).subscribe({ error: () => undefined });
  }

  private createEventId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `lead-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}
